import type {
  BarcodeLookupResult, CachedCategory, CachedCustomer, CachedProduct, CachedProductUnit, CreditSnapshot, CurrentUser,
  DrawerSession, DrawerSummary, HeldOrder, InstalledPrinter, LabelPrinterConfig, LocalSale, LoginResult, PowerStatus,
  PrinterConfig, ReceiptData, SettingsSnapshot, SyncState,
} from './types';

/**
 * Strips Electron IPC error wrappers so the UI sees just the root cause message.
 * Electron wraps thrown errors as "Error invoking remote method 'foo:bar': Error: <real message>".
 */
export function cleanErrorMessage(err: unknown): string {
  const raw = (err instanceof Error ? err.message : String(err)) || 'Unknown error';
  return raw.replace(/^Error invoking remote method '[^']*': (?:Error: )?/, '');
}

/**
 * Wraps every IPC method so any rejected promise has its error message cleaned
 * before it reaches the UI. Non-promise returns (e.g. event subscriber cleanups)
 * pass through unchanged.
 */
function makeCleanBridge(raw: any): any {
  const out: any = {};
  for (const ns of Object.keys(raw)) {
    const group = raw[ns];
    if (group && typeof group === 'object') {
      out[ns] = {};
      for (const name of Object.keys(group)) {
        const fn = group[name];
        if (typeof fn === 'function') {
          out[ns][name] = (...args: any[]) => {
            const r = fn.apply(group, args);
            return r && typeof r.then === 'function'
              ? r.catch((e: unknown) => { throw new Error(cleanErrorMessage(e)); })
              : r;
          };
        } else {
          out[ns][name] = fn;
        }
      }
    } else {
      out[ns] = group;
    }
  }
  return out;
}

let cachedBridge: any = null;
const bridge = (): Window['till'] => {
  if (!cachedBridge && window.till) cachedBridge = makeCleanBridge(window.till);
  return (cachedBridge ?? window.till) as Window['till'];
};

export const api = {
  auth: {
    login: (email: string, password: string): Promise<LoginResult> =>
      bridge().auth.login(email, password),
    logout: (): Promise<boolean> => bridge().auth.logout(),
    me: (): Promise<CurrentUser | null> => bridge().auth.me(),
    changePassword: (currentPassword: string, newPassword: string): Promise<{ ok: boolean }> =>
      bridge().auth.changePassword(currentPassword, newPassword),
    onCleared: (cb: () => void): (() => void) => bridge().auth.onCleared(cb),
    resolveDataMismatch: (choice: 'archive' | 'cancel', serverUrl: string, storeId: string): Promise<unknown> =>
      bridge().auth.resolveDataMismatch(choice, serverUrl, storeId),
  },
  settings: {
    get: (): Promise<SettingsSnapshot> => bridge().settings.get(),
    setApiUrl: (url: string): Promise<boolean> => bridge().settings.setApiUrl(url),
    set: (key: string, value: string): Promise<boolean> => bridge().settings.set(key, value),
  },
  catalog: {
    search: (term: string, opts?: { categoryId?: string | null }): Promise<CachedProduct[]> =>
      bridge().catalog.search(term, opts),
    barcode: (code: string): Promise<BarcodeLookupResult | CachedProduct | null> =>
      bridge().catalog.barcode(code) as Promise<BarcodeLookupResult | CachedProduct | null>,
    productUnits: (productId: string): Promise<CachedProductUnit[]> =>
      bridge().catalog.productUnits(productId),
    customers: (term: string): Promise<CachedCustomer[]> => bridge().catalog.customers(term),
    categories: (): Promise<CachedCategory[]> => bridge().catalog.categories(),
    createCustomer: (dto: { name: string; phone: string; email?: string; notes?: string }):
      Promise<{ id: string; name: string; phone: string; storeId: string | null }> =>
        bridge().catalog.createCustomer(dto),
    creditSnapshot: (customerId: string): Promise<CreditSnapshot> =>
      bridge().catalog.creditSnapshot(customerId),
  },
  sales: {
    create: (payload: {
      // unitPriceOverride/discount: price echo — the charged numbers ride the
      // queue so the server books the sale at what the customer actually paid.
      items: Array<{
        productId: string;
        productUnitId?: string;
        quantity: string;
        unitPriceOverride?: string;
        discount?: string;
      }>;
      payments: Array<{ method: string; amount: string; reference?: string; note?: string }>;
      customerId?: string;
      discountTotal?: string;
      couponCode?: string;
      loyaltyPointsRedeemed?: number;
      storeCreditUsed?: string;
      drawerSessionId?: string;
      heldFromId?: string;
      note?: string;
    }) => bridge().sales.create(payload),
    void: (saleId: string, reason?: string) => bridge().sales.void({ saleId, reason }),
    recent: (filter?: { from?: string; to?: string }): Promise<LocalSale[]> => bridge().sales.recent(filter),
  },
  stock: {
    adjust: (payload: {
      productId: string; productUnitId?: string; quantityDelta: string; movementType: string; note?: string;
    }) => bridge().stock.adjust(payload),
  },
  drawer: {
    open: (openingFloat: string, note?: string): Promise<DrawerSession & { source?: string }> =>
      bridge().drawer.open({ openingFloat, note }),
    close: (countedAmount: string, note?: string) => bridge().drawer.close({ countedAmount, note }),
    movement: (type: string, amount: string, note?: string) =>
      bridge().drawer.movement({ type, amount, note }),
    noSale: (reason: string, note?: string): Promise<{ eventId: string; kicked: boolean }> =>
      bridge().drawer.noSale({ reason, note }),
    activeSessionId: (): Promise<string | null> => bridge().drawer.activeSessionId(),
    summary: (): Promise<DrawerSummary | null> => bridge().drawer.summary(),
  },
  expense: {
    create: (payload: {
      categoryId: string; amount: string; paidFromCashDrawer: boolean;
      description?: string; receiptImageUrl?: string;
    }) => bridge().expense.create(payload),
  },
  heldOrders: {
    save: (label: string | undefined, cart: unknown, note?: string) =>
      bridge().heldOrders.save({ label, cart, note }),
    list: (): Promise<HeldOrder[]> => bridge().heldOrders.list(),
    resume: (id: string): Promise<HeldOrder | null> => bridge().heldOrders.resume(id),
    cancel: (id: string): Promise<boolean> => bridge().heldOrders.cancel(id),
  },
  sync: {
    state: (): Promise<SyncState> => bridge().sync.state(),
    trigger: (): Promise<SyncState> => bridge().sync.trigger(),
    onState: (cb: (s: SyncState) => void) => bridge().sync.onState(cb),
  },
  cache: {
    clearCatalog: (): Promise<SyncState> => bridge().cache.clearCatalog(),
  },
  printer: {
    listPrinters: (): Promise<InstalledPrinter[]> => bridge().printer.listPrinters(),
    getConfig: (): Promise<PrinterConfig> => bridge().printer.getConfig(),
    setConfig: (cfg: PrinterConfig): Promise<PrinterConfig> => bridge().printer.setConfig(cfg),
    test: (): Promise<boolean> => bridge().printer.test(),
    print: (receipt: ReceiptData) => bridge().printer.print(receipt),
    kickDrawer: (): Promise<boolean> => bridge().printer.kickDrawer(),
  },
  labelPrinter: {
    getConfig: (): Promise<LabelPrinterConfig> => bridge().labelPrinter.getConfig(),
    setConfig: (cfg: LabelPrinterConfig) => bridge().labelPrinter.setConfig(cfg),
    test: (): Promise<boolean> => bridge().labelPrinter.test(),
    priceTag: (p: { name: string; sku: string; price: string; barcode?: string | null }) =>
      bridge().labelPrinter.priceTag(p),
  },
  power: {
    status: (): Promise<PowerStatus> => bridge().power.status(),
    onStatus: (cb: (s: PowerStatus) => void) => bridge().power.onStatus(cb),
    onWarning: (cb: (s: PowerStatus) => void) => bridge().power.onWarning(cb),
    onCritical: (cb: (s: PowerStatus) => void) => bridge().power.onCritical(cb),
  },
  queue: {
    stats: (): Promise<unknown> => bridge().queue.stats(),
  },
  app: {
    factoryReset: (): Promise<{ ok: boolean; backupPath: string } | null> =>
      (window.till.app?.factoryReset?.() ?? Promise.resolve(null)),
  },
};
